/**
 * Sistem Ayarları › SMS Hesapları (backlog #637, backend #636 / #631 / #632). E-posta Hesapları sayfasının SMS karşılığı.
 *
 * Satıcı hesabı: ad, gönderen adı, müşteri no (kno), API kullanıcısı (kulad) ve API şifresi. Üç sır YALNIZ yazılır — liste "kayıtlı" rozetleri gösterir
 * (has_kno / has_kulad / has_password), düzenlemede boş bırakılan sır DEĞİŞMEZ. Tek bir varsayılan hesap vardır. Hesap, art arda hatalı bilgi yüzünden devre
 * kesici tarafından ENGELLENDİYSE (sms_account_blocked_at) satır bunu nedeniyle gösterir; bilgiler düzeltilince "Test et ve engeli kaldır" ile yeniden denenir
 * (başarılı test engeli kaldırır). Test SMS'i kredi harcar, güvenli mod geçerlidir ve saatte 5 ile sınırlıdır; numara boşsa isteği yapanın doğrulanmış telefonuna gider.
 * Yazma SYSTEM_SETTINGS_MANAGE ister (403 sayfada görünür).
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, CardBody, Col, Form, FormFeedback, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import SmsSayfa from './SmsSayfa';
import { createAccount, deleteAccount, listAccounts, testAccount, updateAccount } from '../../../../services/smsAyarService';
import { currentLocaleTag } from '../../../../i18n';

const BOS = { sms_account_title: '', sms_account_sender: '', sms_account_kno: '', sms_account_kulad: '', sms_account_sifre: '', is_default: false, is_active: true };
// Satıcının kuralı (backend sms.hata_gonderen_kural): 3-11 karakter, Türkçe harf ve özel karakter yok.
export const gonderenGecerli = (ad) => /^[A-Za-z0-9 ]{3,11}$/.test((ad || '').trim());

const tarih = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleString(currentLocaleTag()) : '';
};

const SmsHesaplariPage = () => {
  const { t } = useTranslation('sms');
  const [hesaplar, setHesaplar] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [mesaj, setMesaj] = useState(null);
  const [form, setForm] = useState(null);          // { id|null, ...alanlar } — açık düzenleme penceresi
  const [kaydediyor, setKaydediyor] = useState(false);
  const [formHatasi, setFormHatasi] = useState(null);
  const [silinecek, setSilinecek] = useState(null);
  const [test, setTest] = useState(null);          // { hesap, numara, sonuc, gonderiyor, hata }

  const yenile = useCallback(async () => {
    try {
      setHesaplar(await listAccounts());
      setHata(null);
    } catch (e) {
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  }, []);

  useEffect(() => {
    let iptal = false;
    async function calistir() {
      try {
        const liste = await listAccounts();
        if (!iptal) setHesaplar(liste);
      } catch (e) {
        if (!iptal) setHata(e.message);
      } finally {
        if (!iptal) setYukleniyor(false);
      }
    }
    calistir();
    return () => { iptal = true; };
  }, []);

  const duzenle = (h) => setForm(h
    ? { id: h.sms_account_id, ...BOS, sms_account_title: h.sms_account_title || '', sms_account_sender: h.sms_account_sender || '', is_default: !!h.is_default, is_active: !!h.is_active,
        _kno: !!h.has_kno, _kulad: !!h.has_kulad, _sifre: !!h.has_password }
    : { id: null, ...BOS });

  const alan = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const yeni = form && form.id === null;
  const eksikSir = form && yeni && (!form.sms_account_kno.trim() || !form.sms_account_kulad.trim() || !form.sms_account_sifre);
  const gonderenHatali = form && form.sms_account_sender.trim() !== '' && !gonderenGecerli(form.sms_account_sender);
  const kaydedilemez = !form || !form.sms_account_title.trim() || eksikSir || gonderenHatali;

  async function kaydet(e) {
    e.preventDefault();
    if (kaydedilemez) return;
    setKaydediyor(true);
    setFormHatasi(null);
    const govde = {
      sms_account_title: form.sms_account_title.trim(),
      sms_account_sender: form.sms_account_sender.trim(),
      is_default: form.is_default,
      is_active: form.is_active,
      // boş sır = değişmez: hiç gönderilmez
      ...(form.sms_account_kno.trim() ? { sms_account_kno: form.sms_account_kno.trim() } : {}),
      ...(form.sms_account_kulad.trim() ? { sms_account_kulad: form.sms_account_kulad.trim() } : {}),
      ...(form.sms_account_sifre ? { sms_account_sifre: form.sms_account_sifre } : {}),
    };
    try {
      if (yeni) await createAccount(govde); else await updateAccount(form.id, govde);
      setForm(null);
      setMesaj(t('hesaplar.kaydedildi'));
      await yenile();
    } catch (err) {
      setFormHatasi(err.message);
    } finally {
      setKaydediyor(false);
    }
  }

  async function sil() {
    const h = silinecek;
    try {
      await deleteAccount(h.sms_account_id);
      setMesaj(t('hesaplar.silindi'));
      await yenile();
    } catch (err) {
      setHata(err.message);
    } finally {
      setSilinecek(null);
    }
  }

  async function testGonder() {
    setTest((s) => ({ ...s, gonderiyor: true, hata: null, sonuc: null }));
    try {
      const sonuc = await testAccount(test.hesap.sms_account_id, test.numara);
      setTest((s) => ({ ...s, gonderiyor: false, sonuc }));
      await yenile();   // başarılı test engeli kaldırır: listedeki durum güncellensin
    } catch (err) {
      setTest((s) => ({ ...s, gonderiyor: false, hata: err.status === 429 ? t('hesaplar.testCokSik') : err.message }));
    }
  }

  const sirRozeti = (var_, ad) => (
    <Badge color={var_ ? 'success-subtle' : 'danger-subtle'} className={var_ ? 'text-success me-1' : 'text-danger me-1'}>{var_ ? t('hesaplar.sirKayitli', { ad }) : t('hesaplar.sirYok', { ad })}</Badge>
  );

  return (
    <SmsSayfa aktif="hesaplar">
      {mesaj && <Alert color="success" fade={false} toggle={() => setMesaj(null)}>{mesaj}</Alert>}
      {hata && <Alert color="danger" fade={false} data-testid="sms-hesaplar-hata">{hata}</Alert>}
      <Card>
        <CardBody>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <p className="text-muted mb-0">{t('hesaplar.aciklama')}</p>
            <Button color="primary" id="sms-hesap-ekle" onClick={() => duzenle(null)}><i className="bx bx-plus me-1" />{t('hesaplar.ekle')}</Button>
          </div>
          {yukleniyor ? <div className="text-center py-4"><Spinner /></div> : (
            <div className="table-responsive">
              <Table hover className="align-middle mb-0" data-testid="sms-hesap-tablosu">
                <thead>
                  <tr><th>{t('hesaplar.ad')}</th><th>{t('hesaplar.gonderen')}</th><th>{t('hesaplar.bilgiler')}</th><th>{t('hesaplar.durum')}</th><th className="text-end">{t('islemler')}</th></tr>
                </thead>
                <tbody>
                  {hesaplar.length === 0 && <tr><td colSpan={5} className="text-center text-muted py-4">{t('hesaplar.bos')}</td></tr>}
                  {hesaplar.map((h) => {
                    const engelli = !!h.sms_account_blocked_at;
                    return (
                      <tr key={h.sms_account_id} data-testid={`sms-hesap-${h.sms_account_id}`}>
                        <td>
                          <span className="fw-semibold">{h.sms_account_title}</span>
                          {h.is_default ? <Badge color="primary" className="ms-2">{t('hesaplar.varsayilan')}</Badge> : null}
                        </td>
                        <td>{h.sms_account_sender || <span className="text-muted">—</span>}</td>
                        <td>{sirRozeti(h.has_kno, 'kno')}{sirRozeti(h.has_kulad, t('hesaplar.kullanici'))}{sirRozeti(h.has_password, t('hesaplar.sifre'))}</td>
                        <td>
                          {engelli ? (
                            <div data-testid="sms-hesap-engelli">
                              <Badge color="danger">{t('hesaplar.engelli')}</Badge>
                              <div className="small text-danger mt-1">{h.sms_account_blocked_reason || t('hesaplar.engelNedeniYok')}</div>
                              <div className="small text-muted">{tarih(h.sms_account_blocked_at)}</div>
                            </div>
                          ) : <Badge color={h.is_active ? 'success' : 'secondary'}>{h.is_active ? t('hesaplar.etkin') : t('hesaplar.pasif')}</Badge>}
                        </td>
                        <td className="text-end text-nowrap">
                          <Button size="sm" color={engelli ? 'warning' : 'light'} className="me-1" id={`sms-test-${h.sms_account_id}`}
                            onClick={() => setTest({ hesap: h, numara: '', sonuc: null, gonderiyor: false, hata: null })}>
                            {engelli ? t('hesaplar.testVeAc') : t('hesaplar.testEt')}
                          </Button>
                          <Button size="sm" color="light" className="me-1" id={`sms-duzenle-${h.sms_account_id}`} onClick={() => duzenle(h)}><i className="bx bx-edit" /></Button>
                          <Button size="sm" color="light" className="text-danger" id={`sms-sil-${h.sms_account_id}`} onClick={() => setSilinecek(h)}><i className="bx bx-trash" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Ekle / düzenle */}
      <Modal isOpen={!!form} toggle={() => !kaydediyor && setForm(null)} centered>
        {form && (
          <Form onSubmit={kaydet} data-testid="sms-hesap-formu">
            <ModalHeader toggle={() => setForm(null)}>{yeni ? t('hesaplar.ekle') : t('hesaplar.duzenle')}</ModalHeader>
            <ModalBody>
              {formHatasi && <Alert color="danger" fade={false}>{formHatasi}</Alert>}
              <div className="mb-3">
                <Label htmlFor="sms-hesap-ad">{t('hesaplar.ad')}</Label>
                <Input id="sms-hesap-ad" value={form.sms_account_title} onChange={alan('sms_account_title')} maxLength={100} />
              </div>
              <div className="mb-3">
                <Label htmlFor="sms-hesap-gonderen">{t('hesaplar.gonderen')}</Label>
                <Input id="sms-hesap-gonderen" value={form.sms_account_sender} onChange={alan('sms_account_sender')} maxLength={32} invalid={!!gonderenHatali} />
                <FormFeedback>{t('hesaplar.gonderenKurali')}</FormFeedback>
                <div className="form-text">{t('hesaplar.gonderenIpucu')}</div>
              </div>
              <Row>
                {[['sms_account_kno', 'kno', 'kno', '_kno', 'text'], ['sms_account_kulad', 'kulad', 'kullaniciAdi', '_kulad', 'text'], ['sms_account_sifre', 'sifre', 'sifre', '_sifre', 'password']].map(([k, kimlik, etiket, var_, tip]) => (
                  <Col md={4} key={k} className="mb-3">
                    <Label htmlFor={`sms-hesap-${kimlik}`}>{etiket === 'kno' ? 'kno' : t(`hesaplar.${etiket}`)}</Label>
                    <Input id={`sms-hesap-${kimlik}`} type={tip} autoComplete="off" value={form[k]} onChange={alan(k)}
                      placeholder={form[var_] ? t('hesaplar.degismez') : ''} />
                  </Col>
                ))}
              </Row>
              <div className="form-text mb-3">{yeni ? t('hesaplar.sirlarGerekli') : t('hesaplar.sirIpucu')}</div>
              <div className="form-check form-switch mb-2">
                <Input className="form-check-input" type="checkbox" role="switch" id="sms-hesap-varsayilan" checked={form.is_default} onChange={alan('is_default')} />
                <Label className="form-check-label" htmlFor="sms-hesap-varsayilan">{t('hesaplar.varsayilanYap')}</Label>
              </div>
              <div className="form-check form-switch">
                <Input className="form-check-input" type="checkbox" role="switch" id="sms-hesap-etkin" checked={form.is_active} onChange={alan('is_active')} />
                <Label className="form-check-label" htmlFor="sms-hesap-etkin">{t('hesaplar.etkin')}</Label>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="light" type="button" onClick={() => setForm(null)}>{t('vazgec')}</Button>
              <Button color="primary" type="submit" id="sms-hesap-kaydet" disabled={kaydediyor || kaydedilemez}>{kaydediyor ? <Spinner size="sm" /> : t('kaydet')}</Button>
            </ModalFooter>
          </Form>
        )}
      </Modal>

      {/* Sil */}
      <Modal isOpen={!!silinecek} toggle={() => setSilinecek(null)} centered>
        <ModalHeader toggle={() => setSilinecek(null)}>{t('hesaplar.silBasligi')}</ModalHeader>
        <ModalBody>{silinecek && t('hesaplar.silSorusu', { ad: silinecek.sms_account_title })}</ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setSilinecek(null)}>{t('vazgec')}</Button>
          <Button color="danger" id="sms-sil-onayla" onClick={sil}>{t('sil')}</Button>
        </ModalFooter>
      </Modal>

      {/* Test */}
      <Modal isOpen={!!test} toggle={() => !test?.gonderiyor && setTest(null)} centered>
        {test && (
          <>
            <ModalHeader toggle={() => setTest(null)}>{t('hesaplar.testBasligi', { ad: test.hesap.sms_account_title })}</ModalHeader>
            <ModalBody>
              {test.hesap.sms_account_blocked_at && <Alert color="warning" fade={false} className="small">{t('hesaplar.testEngelliNot')}</Alert>}
              <p className="text-muted small">{t('hesaplar.testAciklama')}</p>
              <Label htmlFor="sms-test-numara">{t('hesaplar.testNumara')}</Label>
              <Input id="sms-test-numara" type="tel" placeholder="0532 123 45 67" value={test.numara} onChange={(e) => setTest((s) => ({ ...s, numara: e.target.value }))} />
              <div className="form-text">{t('hesaplar.testNumaraIpucu')}</div>
              {test.hata && <Alert color="danger" fade={false} className="mt-3 mb-0" data-testid="sms-test-hata">{test.hata}</Alert>}
              {test.sonuc && (
                <Alert color={test.sonuc.status === 'SENT' ? 'success' : 'danger'} fade={false} className="mt-3 mb-0" data-testid="sms-test-sonuc">
                  <strong>{t(`durum.${test.sonuc.status}`, { defaultValue: test.sonuc.status })}</strong>
                  {test.sonuc.to ? <span className="ms-2">{test.sonuc.to}</span> : null}
                  {(test.sonuc.error || test.sonuc.message) && <div className="small mt-1">{test.sonuc.error || test.sonuc.message}</div>}
                </Alert>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="light" onClick={() => setTest(null)}>{t('kapat')}</Button>
              <Button color="primary" id="sms-test-gonder" disabled={test.gonderiyor} onClick={testGonder}>{test.gonderiyor ? <Spinner size="sm" /> : t('hesaplar.testGonder')}</Button>
            </ModalFooter>
          </>
        )}
      </Modal>
    </SmsSayfa>
  );
};

export default SmsHesaplariPage;
